import { useCallback } from 'react';
import type { AxiosResponse } from 'axios';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type * as Projects from '@trinity/chat/api/interfaces/projects';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';

type Data = Projects.GetProjectConferenceTagSummaries.Response;
type Params = IProjectId;
type QueryKey = readonly [string, Params];

export function useProjectConferenceTagSummaries(
  data: Params,
  options?: UseQueryOptions<Data, AxiosResponse, Data, QueryKey>,
) {

  const api = useChatApiClient();

  const qk = projectTagSummariesQueryKey(data);

  return useQuery({
    queryKey: qk, queryFn: ({ queryKey }) => {
      const { projectId } = queryKey[1];

      return api.projects.getTagSummaries({ projectId });

    },
    initialData: {
      meta: {
        canGenerateSummary: false,
      },
      items: [],
    },
    refetchOnWindowFocus: false,
    ...options,
  });
}

export const projectTagSummariesQueryKey = (data: Params): QueryKey => ['get:projects/conference-tags/summaries', data];

type Updater = (old: Projects.GetProjectConferenceTagSummaries.Response) => Projects.GetProjectConferenceTagSummaries.Response;

export const useUpdateStoredProjectTagSummaries = ({ projectId }: Params) => {
  const qk = projectTagSummariesQueryKey({ projectId });

  const queryClient = useQueryClient();

  const handleUpdate = useCallback((updater: Updater) => {
    queryClient.setQueryData(qk, updater);
  }, [queryClient, qk]);

  return handleUpdate;
};

export const useInvalidateProjectTagSummaries = (props: Params) => {
  const qk = projectTagSummariesQueryKey(props);

  const queryClient = useQueryClient();

  const handleInvalidate = useCallback(() => {
    return queryClient.invalidateQueries({ queryKey: qk });
  }, [queryClient, qk]);

  return handleInvalidate;
};